import { useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';

/**
 * Weidner Via6 PropertyOverview override.
 *
 * Forked from the rise PropertyOverview to:
 *   - Switch the centered, single-column layout to an asymmetric
 *     1/3 (heading) — 2/3 (blurb + CTAs) grid on >=lg viewports.
 *     Mobile/tablet stack vertically (heading above blurb).
 *   - Replace the single "Explore Floor Plans" CTA with a pair of
 *     CTAs underneath the blurb: a primary "Apply Now" (defaults to
 *     `property_apply_now_url`) and a secondary "View Gallery"
 *     (defaults to `/gallery`).
 *   - Drop the centered accent bar and gradient-clipped title so the
 *     left column reads as a clean editorial heading block.
 *
 * Scroll-reveal hooks from the rise parent are dropped here — keeps
 * this fork import-light and matches the simpler motion language used
 * in the Weidner Hero override.
 */

const applyCtaDef = {
  label: 'Apply Now CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Label', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const galleryCtaDef = {
  label: 'View Gallery CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const applyCta = useSlotGroup('propertyoverview.apply_cta', applyCtaDef);
  const galleryCta = useSlotGroup('propertyoverview.gallery_cta', galleryCtaDef);

  // Lazy-load the "Satisfy" cursive Google Font used by via6living.com
  // as an accent face. Matches the de-dupe pattern used in Header.tsx.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="satisfy"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Satisfy&display=swap';
    link.setAttribute('data-vibe-font', 'satisfy');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="py-20 md:py-28 bg-[var(--vibe-color-primary)]">
      {/* Cursive accent rule for the "life" span in the title. Scoped to a
          single class so it only fires when the richtext defaultValue (or
          an editor-authored override) wraps a word in this span. */}
      <style>{`
        .vibe-cursive-accent {
          font-family: 'Satisfy', cursive;
          color: var(--vibe-color-secondary);
          font-weight: 400;
          text-transform: none;
          letter-spacing: 0;
        }
      `}</style>
      <div className="max-w-7xl xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Left 2/5 — heading */}
          <div className="lg:col-span-2">
            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="font-heading text-4xl sm:text-5xl md:text-6xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight uppercase [&_strong]:font-normal [&_b]:font-normal [&_br]:hidden lg:[&_br]:inline"
            />
          </div>

          {/* Right 3/5 — blurb + CTAs */}
          <div className="lg:col-span-3">
            <RichTextContent
              path="propertyoverview.description"
              as="div"
              className="font-body text-sm sm:text-base font-normal text-[var(--vibe-color-text-on-primary)] leading-relaxed [&_a]:text-[var(--vibe-color-text-on-primary)] [&_a]:underline [&_strong]:font-normal [&_b]:font-normal"
            />

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <EditableSlotGroup
                groupPath="propertyoverview.apply_cta"
                definition={applyCtaDef}
              >
                <a
                  href={applyCta.url || '#'}
                  target={applyCta._target || '_self'}
                  className="inline-block text-center w-[18rem] px-6 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-secondary)] hover:bg-white hover:text-[var(--vibe-color-secondary)] hover:border-white transition-all"
                >
                  {applyCta.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup
                groupPath="propertyoverview.gallery_cta"
                definition={galleryCtaDef}
              >
                <a
                  href={galleryCta.url || '/gallery'}
                  target={galleryCta._target || '_self'}
                  className="inline-block text-center w-[18rem] px-6 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full border border-[var(--vibe-color-text-on-primary)] text-[var(--vibe-color-text-on-primary)] hover:bg-[var(--vibe-color-secondary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-white transition-all"
                >
                  {galleryCta.label || 'View Gallery'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Enjoy the <span class="vibe-cursive-accent">life</span> you deserve at <span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.apply_cta': applyCtaDef,
    'propertyoverview.gallery_cta': galleryCtaDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
